/** @format */

import {
  configureStore,
  ThunkAction,
  Action,
  Middleware,
  combineReducers,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";
import resourcesReducer from "../features/resources/resourcesSlice";
import authReducer from "../features/auth/authSlice";
import notificationsReducer, {
  setAddNotifications,
} from "../features/notifications/notificationsSlice";
import { apiSlice } from "./apiSlice";
import featureFlagReducer from "../features/featureFlag/featureFlagSlice";
import { setCookie } from "../utils/cookies";

const storageKey = "BL_CMS_STATE";

const saveTokenToLocalStorageMiddleware: Middleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    const beforeToken = getState().auth.userToken;
    const result = next(action);
    const afterToken = getState().auth.userToken;
    if (beforeToken?.token !== afterToken?.token) {
      const stringyToken = JSON.stringify({ userToken: afterToken });
      try {
        localStorage.setItem(storageKey, stringyToken);
        process.env.REACT_APP_FLAG_COOKIE ?? setCookie("user_token", stringyToken, 7);
      } catch (e: any) {
        alert(
          "There was an error saving to your browser. You may need to delete some old resources to free up space. " +
            e?.message,
        );
      }
    }
    return result;
  };

// Listen for mutation errors and create toast notification on failure
const mututationErrorMiddleware: Middleware = (store) => (next) => (action) => {
  if (
    action.type.includes("api/executeMutation/") &&
    !isPending(action) &&
    isRejected(action)
  ) {
    const meta = action.meta;
    const method = meta.baseQueryMeta.request.method;
    const endpointNameToReadable = meta.arg.endpointName
      .split(/(?<![A-Z])(?=[A-Z])/)
      .join(" ")
      .toLocaleLowerCase();

    store.dispatch(
      setAddNotifications({
        date: meta.fulfilledTimeStamp,
        id: meta.requestId,
        title:
          method === "POST"
            ? "Error on save"
            : method === "DELETE"
            ? "Error on delete"
            : "Error",
        msg: `Failed to ${endpointNameToReadable}`,
        type: "danger",
      }),
    );
  }

  return next(action);
};

const savedState = JSON.parse(localStorage.getItem(storageKey) || "{}");

const rootReducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  resources: resourcesReducer,
  auth: authReducer,
  featureFlag: featureFlagReducer,
  notifications: notificationsReducer,
});

export function setupStore(preloadedState?: Partial<RootState>) {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        apiSlice.middleware,
        saveTokenToLocalStorageMiddleware,
        mututationErrorMiddleware,
      ),
    preloadedState: {
      auth: {
        status: "idle",
        userToken: savedState.userToken,
      },
      ...preloadedState,
    },
  });
  return store;
}

export const store = setupStore();
export type AppDispatch = AppStore["dispatch"];
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

export const EXPORT_FOR_TESTING = { mututationErrorMiddleware };
